import React from 'react';
import { Box } from '@mui/material';

/**
 * Props for the SkipGuide component
 */
interface SkipGuideProps {
  /**
   * Click handler for the skip action
   */
  onClick: () => void;
  /**
   * Optional text to display (defaults to "Skip guide")
   */
  children?: React.ReactNode;
  /**
   * Optional sx prop for additional styling
   */
  sx?: any;
}

/**
 * SkipGuide component - A standardized skip button for walkthrough guides
 * 
 * This component provides consistent styling for skip guide buttons across the application,
 * with hover effects and theme-aware styling.
 * 
 * @example
 * ```tsx
 * <SkipGuide onClick={handleSkip} />
 * <SkipGuide onClick={handleSkip}>Skip tutorial</SkipGuide>
 * ```
 */
const SkipGuide: React.FC<SkipGuideProps> = ({ 
  onClick, 
  children = 'Skip guide',
  sx
}) => {
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        background: 'transparent',
        border: 'none',
        color: '#878787',
        cursor: 'pointer',
        fontSize: '12px',
        padding: 0,
        textDecoration: 'underline',
        lineHeight: "130%",
        letterSpacing: "-0.24px",
        fontFamily: '"Open Sans"',
        fontWeight: 400,
        '&:hover': { 
          color: '#0e3d82',
          textDecoration: 'underline'
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

export default SkipGuide;